// Stable anchor ids for the web-user "Helpmeet" guided-help tours.
//
// A guided-help step targets a real control with `document.querySelector('[data-help-anchor="…"]')`.
// The ids live here — a single source of truth shared by the components that render the controls
// (in @keydials/dial-workspace) and the web-user help catalog that drives the tours — so the two
// can never drift. The attribute is inert: it does nothing unless a running tour queries it, so
// adding it to shared components has no effect on other consumers (web-admin renders the shared dial
// components too, and has no tours at all).

export const HELP_ANCHORS = {
  // The dial itself (dial-workspace). Only the interactive one — the dashboard's cards and the login hero
  // render the same component, and a tour must never spotlight a thumbnail.
  dialFace: "dial-face",

  // Dial chrome (web-user)
  dialSubscribe: "dial-subscribe",
  dialSettings: "dial-settings",
  dialLogo: "dial-logo",

  // Dial Settings menu entries (web-user). Every dial setting arrives through this menu now, so a tour
  // that used to point at a top-bar button points at one of these instead.
  dialPeople: "dial-people",
  dialGuests: "dial-guests",
  dialLicensing: "dial-licensing",
  dialEditStructure: "dial-edit-structure",
  /** The Calendars entry, on a dial with a section of days (KEY-736). */
  dialCalendars: "dial-calendars",
  /** The licensing figures themselves, once Licensing is open — not the menu entry that opens them. */
  dialLicensingSummary: "dial-licensing-summary",
  /** Where your own calendar is put on this dial (or the note saying why it can't be yet). */
  calendarOnDial: "calendar-on-dial",

  /** Which level of the dial is open — the chip inside ScopePill, which is the ONE thing rendered in both
   * the Classic and the Dynamic workspace (KEY-531/532). The Classic-only header card around it is not. */
  scopeLevel: "scope-level",

  /** The bell on a section's row in the dial-level Sections list — following one part instead of all of it. */
  sectionRowSubscribe: "section-row-subscribe",
  /** The Subscribe button on a section's own panel, beside its name. */
  sectionSubscribe: "section-subscribe",

  // People section (web-user)
  /** Opens the slide-over that the member-grant fields live in. */
  memberGiveAccess: "member-give-access",

  // Guest invite form (web-user)
  guestEmail: "guest-email",
  guestInvite: "guest-invite",

  // Action-point panel (dial-workspace)
  apTabItems: "ap-tab-items",
  apTabFiles: "ap-tab-files",
  apTabDiscussion: "ap-tab-discussion",
  apTabGuidance: "ap-tab-guidance",
  /** The action point's own fields, moved off the bottom of Overview onto their own tab (KEY-485). */
  apTabInfo: "ap-tab-info",
  /** The Overview tab's list of what the action point asks for, and what's still unanswered (KEY-569). */
  apOverviewChecklist: "ap-overview-checklist",
  /** The trail of what changed here, and who changed it (KEY-464). */
  apTabActivity: "ap-tab-activity",
  apUploadEvidence: "ap-upload-evidence",
  apStatus: "ap-status",
  /** An item's name in the Items list — clicking it opens the item's card. */
  itemRowToggle: "item-row-toggle",
  /** Opens the sheet that sets a review date on a status (KEY-376). */
  apScheduleReview: "ap-schedule-review",
  /** The date field inside that sheet. */
  apReviewDate: "ap-review-date",
  /** The section's own status pill on the section panel (KEY-479). */
  sectionStatus: "section-status",
  /** The dial's own Guidance tab (KEY-534). */
  dialTabGuidance: "dial-tab-guidance",
  /** The dial's read-only transcript of everything discussed on it (KEY-559). */
  dialTabDiscussion: "dial-tab-discussion",
  /** The section page's own tabs (KEY-460 / KEY-464 / KEY-542 / KEY-559). */
  sectionTabGuidance: "section-tab-guidance",
  sectionTabDiscussion: "section-tab-discussion",
  sectionTabFiles: "section-tab-files",
  sectionTabActivity: "section-tab-activity",
  apSubscribe: "ap-subscribe",
  messageEditor: "message-editor",
  messageSend: "message-send",

  // Members modal (web-user)
  memberUserSelect: "member-user-select",
  memberRoleSelect: "member-role-select",
  memberAddGrant: "member-add-grant",

  // Centre-logo editor (dial-workspace)
  logoUpload: "logo-upload",

  // App chrome (web-user) — the account menu, and the page it opens.
  accountMenu: "account-menu",
  /** "Settings" in the account menu, which opens Your settings. */
  accountSettingsLink: "account-settings-link",
  /** The Connected calendars card on Your settings (KEY-736). */
  connectedCalendars: "connected-calendars",
  accountPhone: "account-phone",
  /** Opens the "how do I…" drawer. Self-referential in the first-run tour, which uses it to say where the
   * tour itself can be started again. */
  helpButton: "help-button",
  feedbackButton: "feedback-button",
} as const;

export type HelpAnchor = (typeof HELP_ANCHORS)[keyof typeof HELP_ANCHORS];

/**
 * Spread onto a DOM element to make it targetable by the Helpmeet guided-help tours, e.g.
 * `<button {...helpAnchor(HELP_ANCHORS.apStatus)}>`. Kept as a helper so the attribute name is
 * written in exactly one place.
 */
export function helpAnchor(id: HelpAnchor): { "data-help-anchor": HelpAnchor } {
  return { "data-help-anchor": id };
}

/**
 * The visible (or accessible) label of the control each anchor points at.
 *
 * This exists because of the failure the help catalog is written against: a control keeps its anchor but is
 * RENAMED, so the tour still resolves and still runs, and quietly says "Items" about a tab now labelled
 * "Checks". The anchor id alone can't catch that — it is an internal string nobody renames when the wording
 * changes. Two tests read this: one asserts each tour step's prose still quotes its control's label, and one
 * (in @keydials/dial-workspace, which has a DOM) asserts the anchored element still renders it. Rename the
 * control and both fail, which is the point.
 *
 * Matched case-insensitively against the element's text, `aria-label` and `title`, because several of these
 * controls are icon-only and carry their wording in the accessible name rather than in text.
 *
 * Deliberately partial: an entry is a promise that the word is on screen, so only anchors whose control
 * really does say something are listed.
 */
export const ANCHOR_LABELS: Partial<Record<HelpAnchor, string>> = {
  [HELP_ANCHORS.dialFace]: "dial",
  [HELP_ANCHORS.scopeLevel]: "Whole dial",
  [HELP_ANCHORS.dialSubscribe]: "Subscribe",
  [HELP_ANCHORS.sectionRowSubscribe]: "Subscribe",
  [HELP_ANCHORS.apStatus]: "status",
  [HELP_ANCHORS.apTabItems]: "Items",
  [HELP_ANCHORS.dialTabDiscussion]: "Discussion",
  [HELP_ANCHORS.sectionTabDiscussion]: "Discussion",
  [HELP_ANCHORS.helpButton]: "Help",
  [HELP_ANCHORS.feedbackButton]: "Feedback",
};

/** Which level of a dial is open. */
export type ScopeLevel = "dial" | "section" | "action";

/**
 * The word for each level — WHOLE DIAL / SECTION / ACTION POINT.
 *
 * Lives here, rather than beside the chip that shows it, so the guided-help tour can BUILD its prose from the
 * same constant instead of retyping the words. Renaming a level then changes the chip and the tour in one
 * edit; the alternative is prose that drifts silently, which is the whole hazard {@link ANCHOR_LABELS}
 * exists to catch. `@keydials/dial-workspace`'s ScopeHeader re-exports it, so its own consumers are unmoved.
 */
export const SCOPE_LEVEL_WORD: Record<ScopeLevel, string> = {
  dial: "Whole dial",
  section: "Section",
  action: "Action point",
};
